import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { InfoSlide } from '@/components/onboarding/InfoSlide'
import { SignupSlide } from '@/components/onboarding/SignupSlide'
import { LoginSlide } from '@/components/onboarding/LoginSlide'
import { useAnalytics } from '@/hooks/useAnalytics'
import { ANALYTICS_EVENTS } from '@/analytics/analyticsEvents'

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const [currentStep, setCurrentStep] = useState(0)
  const [canSubmitForm, setCanSubmitForm] = useState(false)
  const [showingReferralPrompt, setShowingReferralPrompt] = useState(false)
  const [isOnLoginSlide, setIsOnLoginSlide] = useState(false)
  const [isInPasswordReset, setIsInPasswordReset] = useState(false)
  const [isPasswordResetConfirmation, setIsPasswordResetConfirmation] = useState(false)
  const [slideDirection, setSlideDirection] = useState<'forward' | 'backward'>('forward')
  
  const slides = [
    {
      id: 'feed',
      type: 'info' as const,
      image: '/assets/onboarding/feed.png',
      title: 'Scroll flashes and tattoos by local artists'
    },
    {
      id: 'guests',
      type: 'info' as const,
      image: '/assets/onboarding/guests.png',
      title: "See who's coming to guest at your city"
    },
    {
      id: 'artist',
      type: 'info' as const,
      image: '/assets/onboarding/artist_profile.png',
      title: 'Find your next tattoo artist'
    },
    {
      id: 'chat',
      type: 'info' as const,
      image: '/assets/onboarding/chat.png',
      title: 'Book the tattoo right in the app'
    },
    {
      id: 'signup',
      type: 'signup' as const
    }
  ]

  const currentSlide = slides[currentStep] || null
  const isSignupStep = currentSlide?.type === 'signup'

  // Helper function to get step title for analytics
  const getStepTitle = (stepIndex: number, isLogin: boolean): string => {
    if (isLogin) {
      return 'login'
    }
    const slide = slides[stepIndex]
    if (!slide) return 'unknown'
    if (slide.type === 'signup') {
      return 'signup'
    }
    if (slide.type === 'info') {
      return `info_${slide.id}`
    }
    return 'unknown'
  }

  // Track onboarding started on mount
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.USER_ONBOARDING_STARTED, {
      total_steps: slides.length
    })
  }, [trackEvent])

  // Track step views whenever currentStep or isOnLoginSlide changes
  useEffect(() => {
    const stepIndex = isOnLoginSlide ? slides.length : currentStep
    const stepTitle = getStepTitle(currentStep, isOnLoginSlide)
    const navigationDirection = slideDirection === 'forward' ? 'forward' : 'backward'

    trackEvent(ANALYTICS_EVENTS.USER_ONBOARDING_STEP_VIEWED, {
      step_index: stepIndex,
      step_title: stepTitle,
      navigation_direction: navigationDirection
    })
  }, [currentStep, isOnLoginSlide, slideDirection, trackEvent])

  const handleNext = () => {
    if (currentStep < slides.length - 1) {
      setSlideDirection('forward')
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    setSlideDirection('backward')
    if (isOnLoginSlide) {
      setIsOnLoginSlide(false)
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleLogin = () => {
    setSlideDirection('forward')
    setIsOnLoginSlide(true)
  }

  const handleAuthSuccess = () => {
    // Track onboarding exit
    const exitType = isOnLoginSlide ? 'login' : 'signup'
    const completedStepIndex = isOnLoginSlide ? slides.length : currentStep
    const completedStepTitle = getStepTitle(currentStep, isOnLoginSlide)
    
    trackEvent(ANALYTICS_EVENTS.USER_ONBOARDING_EXITED, {
      exit_type: exitType,
      completed_step_index: completedStepIndex,
      completed_step_title: completedStepTitle
    })

    // Mark onboarding as complete
    localStorage.setItem('onboarding-completed', 'true')
    console.log('[OnboardingPage] Auth success, navigating to feed')
    
    // Navigate to feed with replace to prevent back navigation to onboarding
    navigate('/feed', { replace: true })
  }

  const handlePasswordResetChange = (isInReset: boolean, isConfirmation: boolean) => {
    setIsInPasswordReset(isInReset)
    setIsPasswordResetConfirmation(isConfirmation)
  }

  return (
    <div className="fixed inset-0 bg-background flex flex-col overflow-hidden">
      <div className="phone-container-padded w-full flex flex-col h-full">
        {/* Progress Indicators - At top - Hide when on login slide */}
        {!isOnLoginSlide && (
          <div className="flex justify-center gap-2 pt-6 pb-4 flex-shrink-0">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`h-1 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-primary'
                    : 'w-8 bg-muted'
                }`}
              />
            ))}
          </div>
        )}

        {/* Slides Container - Takes remaining space */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div 
            key={isOnLoginSlide ? 'login' : currentStep}
            className={`h-full animate-in fade-in duration-300 ${
              slideDirection === 'forward' ? 'slide-in-from-right-4' : 'slide-in-from-left-4'
            }`}
          >
            {isOnLoginSlide ? (
              <LoginSlide 
                onSuccess={handleAuthSuccess}
                onFormValidChange={setCanSubmitForm}
                onPasswordResetChange={handlePasswordResetChange}
              />
            ) : (
              <>
                {currentSlide?.type === 'info' && 'image' in currentSlide && (
                  <InfoSlide
                    image={currentSlide.image}
                    title={currentSlide.title}
                  />
                )}
                
                {currentSlide?.type === 'signup' && (
                  <SignupSlide 
                    onSuccess={handleAuthSuccess} 
                    onLogin={handleLogin}
                    onFormValidChange={setCanSubmitForm}
                    onReferralPromptChange={setShowingReferralPrompt}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Bottom Navigation - Show unless showing referral prompt */}
        {!showingReferralPrompt && (
          <div className="p-6 pb-8 flex-shrink-0">
            <div className="flex gap-3 w-full">
              <Button
                onClick={isOnLoginSlide ? handleBack : (currentStep === 0 ? handleLogin : handleBack)}
                variant="secondary"
                className="flex-1 h-14 text-base font-medium rounded-[0.6rem]"
              >
                {isOnLoginSlide || currentStep > 0 ? 'Back' : 'Login'}
              </Button>
              <Button
                onClick={
                  isPasswordResetConfirmation ? handleBack :
                  (!isOnLoginSlide && !isSignupStep ? handleNext : undefined)
                }
                disabled={(isSignupStep || (isOnLoginSlide && !isPasswordResetConfirmation)) && !canSubmitForm}
                className="flex-1 h-14 text-base font-medium rounded-[0.6rem]"
                form={
                  isSignupStep ? 'signup-form' : 
                  (isOnLoginSlide && isInPasswordReset && !isPasswordResetConfirmation ? 'password-reset-form' : 
                  (isOnLoginSlide && !isInPasswordReset ? 'login-form' : undefined))
                }
                type={(isSignupStep || (isOnLoginSlide && !isPasswordResetConfirmation)) ? 'submit' : 'button'}
              >
                {isOnLoginSlide ? 
                  (isInPasswordReset ? 
                    (isPasswordResetConfirmation ? 'Back to login' : 'Send') 
                    : 'Login') 
                  : (isSignupStep ? "Let's go!" : 'Next')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
